import { partition, uniqBy } from 'lodash';

import { PropertyAsset } from '@funnels/types';

import { INITIAL_EXPENSES } from './initialFormValues';

export type PropertyInGroup = {
  applicantIds: string[];
  properties: PropertyAsset[];
};

function getApplicantIdsFromProperty(property: PropertyAsset) {
  return property.percentOwned?.owners.map((owner) => owner.applicantId) || [];
}

function isPropertyOwnedBy(applicantId: string) {
  return (property: PropertyAsset) =>
    property.percentOwned?.owners.some((owner) => owner.applicantId === applicantId) || false;
}

function getSingleApplicantIds(property: PropertyAsset, marriedApplicantIds: string[]): string[] {
  return (
    property.percentOwned?.owners
      .map((owner) => owner.applicantId)
      .filter((applicantId) => !marriedApplicantIds.includes(applicantId)) || []
  );
}

function groupSingleOwnedProperties(properties: PropertyAsset[], marriedApplicantIds: string[]): PropertyInGroup[] {
  const grouped = properties.reduce((result, property) => {
    const applicantIds = getSingleApplicantIds(property, marriedApplicantIds);

    for (const applicantId of applicantIds) {
      if (result[applicantId]) {
        result[applicantId].push(property);
      } else {
        result[applicantId] = [property];
      }
    }

    return result;
  }, {} as { [applicantId: string]: PropertyAsset[] });

  return Object.entries(grouped).map(([applicantId, group]) => ({
    applicantIds: [applicantId],
    properties: group,
  }));
}

export default function groupPropertiesByMaritalStatus(
  properties: PropertyAsset[],
  marriedApplicantIds: string[]
): PropertyInGroup[] {
  // no married applicants
  if (marriedApplicantIds.length < 2) {
    return groupSingleOwnedProperties(properties, []);
  }

  const [marriedOwnedProperties, singleOwnedProperties] = partition(properties, (property) => {
    const applicantIds = getApplicantIdsFromProperty(property);
    return applicantIds.some((id) => marriedApplicantIds.includes(id));
  });

  const groupedMarriedOwnedProperties = [] as PropertyInGroup[];
  // no married expenses
  if (marriedOwnedProperties.length === 0) {
    return groupSingleOwnedProperties(properties, []);
  }

  let index = 0;
  while (index < marriedApplicantIds.length - 1) {
    const currentMarriedId1 = marriedApplicantIds[index];
    const currentMarriedId2 = marriedApplicantIds[index + 1];
    index = index + 2;
    const currentMarriedProperties1 = marriedOwnedProperties.filter(isPropertyOwnedBy(currentMarriedId1));
    const currentMarriedProperties2 = marriedOwnedProperties.filter(isPropertyOwnedBy(currentMarriedId2)) || {
      ...INITIAL_EXPENSES(),
      applicantId: currentMarriedId2,
    };

    const mergedProperties = uniqBy(
      [...currentMarriedProperties1, ...currentMarriedProperties2],
      (property) => property.clientId
    );

    groupedMarriedOwnedProperties.push({
      applicantIds: [currentMarriedId1, currentMarriedId2],
      properties: mergedProperties,
    });
  }

  const groupedSingleOwnedProperties: PropertyInGroup[] = groupSingleOwnedProperties(properties, marriedApplicantIds);
  return [...groupedMarriedOwnedProperties, ...groupedSingleOwnedProperties] as PropertyInGroup[];
}
